import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
// Import Shared Components
import { ButtonGroup, Loader, Input } from 'shared-components';
import { isHRB } from 'shared-components/src/utils/whitelabel';
// Import Shared Utils
import { formatExpiryDate } from 'shared-components/src/utils/formatDateTime';
import { post } from 'shared-components/src/utils/http';
import { assignSafeUrl } from 'shared-components/src/utils/safeUrl';
// Import translations
import { profileEn } from '../../../../i18n/profile';
// Import Global Components
import Title from '../../../../components/Title';
import Button from '../../../../components/Button';
import Alert from '../../../../components/Alert';
import Text from '../../../../components/Text';
import {
  PinUnblockBanner,
  UnblockPinButton,
  UnblockPinModal,
} from '../../../../components/PinUnblock';
// Import Global Layouts
import Form from '../../../../layouts/Form';
import InputGroup from '../../../../layouts/InputGroup';
import InputRow from '../../../../layouts/InputRow';
// Import Global Hook
import useAlert from '../../../../hooks/useAlert';
import usePinUnblock from '../../../../hooks/usePinUnblock';
// Import Local Components
import GetPinModal from '../GetPinModal';
import GetGalileoPinModal from '../GetGalileoPinModal';
import SetPinModal from '../SetPinModal';
import PasswordConfirmModal, {
  isStepUpRequiredError,
  reverifyPassword,
} from '../PasswordConfirmModal';
// Import Local styles
import { LoaderContainer, SensitiveDataFrame } from './styles';
import { TitleWrapper } from '../Form/styles';

const CardInfo = ({
  currentAccount,
  programProcessor,
  programConfigs,
  onPinUnblocked,
  onPinChanged,
}) => {
  const intl = useIntl();
  // modal states
  const [showGetPinModal, setShowGetPinModal] = useState(false);
  const [showSetPinModal, setShowSetPinModal] = useState(false);
  // component states
  const [pinModalAlert, setPinModalAlert] = useAlert();
  const [pin, setPin] = useState(null);
  const [cardInfo, setCardInfo] = useState({
    cardNumber: '--',
    expiryDate: '--',
    cvv: '--',
  });
  const [cardInfoIsLoading, setCardInfoIsLoading] = useState(true);
  // iframe-enabled programs render sensitive data in a hosted iframe instead
  const [iframeUrl, setIframeUrl] = useState(null);
  const [showCardInfoErrorAlert, setShowCardInfoErrorAlert] = useAlert();
  const [account, setAccount] = useState(null);
  // step-up re-password prompt for the sensitive-data mint
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordConfirmError, setPasswordConfirmError] = useState(null);
  // GetPinModal is unmounted & remounted whenever an error occurs
  // to ensure useEffect within GetPinModal only run onces, use initialPinFetch
  const initialPinFetch = useRef(true);
  // Self-service PIN unlock lives in the panel that already owns PIN self-service
  const pinUnblock = usePinUnblock(currentAccount, {
    onUnblocked: onPinUnblocked,
  });

  // On initial render, fetch info for cardholder
  useEffect(() => {
    setCardInfoIsLoading(false);
    setAccount(currentAccount);
    setCardInfo(prev => ({
      ...prev,
      cardNumber: currentAccount.cards[0].number || '--',
    }));
  }, [setShowCardInfoErrorAlert]);

  const toggleGetPinModal = e => {
    e.preventDefault();
    if (!showGetPinModal) {
      // Re-arm the modal's one-shot fetch: PIN iframe tokens are single-use,
      // so every open needs a fresh mint (and step-up prompt if unstamped)
      initialPinFetch.current = true;
    }
    setShowGetPinModal(!showGetPinModal);
  };

  const toggleSetPinModal = () => {
    // Open on a clean slate: the modal alert is shared with GetPinModal, and an
    // error left from an earlier attempt would otherwise greet the next one.
    if (!showSetPinModal) setPinModalAlert(null);
    setShowSetPinModal(!showSetPinModal);
  };

  const mintSensitiveData = async (isRetry = false) => {
    setCardInfoIsLoading(true);
    try {
      // First fetch one-time token for sensitive info
      const tokenRes = await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/sensitive_data`,
        { last_four_digits: account.cards[0].last_four_digits }
      );
      // Iframe-enabled programs return a hosted iframe URL; render it and
      // never pull sensitive data into the browser
      if (tokenRes.data.iframe_url) {
        setIframeUrl(tokenRes.data.iframe_url);
        setShowCardInfoErrorAlert(null);
        setCardInfoIsLoading(false);
        return;
      }
      const {
        data: { token },
      } = tokenRes;
      // Exchange token for sensitive info
      const { data: sensitiveCardInfo } = await post(
        '/api/v1/cardholder_portal/cardholder/accounts/sensitive_data',
        { token }
      );
      const formattedSensitiveCardInfo = {
        cardNumber: sensitiveCardInfo.card_number,
        cvv: sensitiveCardInfo.cvv,
        expiryDate: formatExpiryDate(sensitiveCardInfo.expiry_date),
      };
      setCardInfo(formattedSensitiveCardInfo);
      setShowCardInfoErrorAlert(null);
    } catch (e) {
      // Step-up: prompt for the password, then retry the mint once
      if (!isRetry && isStepUpRequiredError(e)) {
        setPasswordConfirmError(null);
        setShowPasswordConfirm(true);
      } else {
        setShowCardInfoErrorAlert({ type: 'danger', message: e.message });
      }
    }
    setCardInfoIsLoading(false);
  };

  const fetchSensitive = async e => {
    e.preventDefault();
    await mintSensitiveData();
  };

  const handlePasswordConfirm = async password => {
    try {
      await reverifyPassword(password);
    } catch {
      setPasswordConfirmError(profileEn['profile-confirm-password-error']);
      return;
    }
    setShowPasswordConfirm(false);
    setPasswordConfirmError(null);
    await mintSensitiveData(true);
  };

  const fetchGalileoPinForm = async e => {
    e.preventDefault();

    setCardInfoIsLoading(true);
    try {
      // Galileo direct render form allows redirect to another URL upon submit
      const callbackDomain =
        /* Uncomment the two lines of code following this comment to develop direct render in local environment. // uncomment for local pin change
       The following two lines are needed in local because Galileo will return an error webpage, if localhost is passed in as callback_url.
       To circumvent, pass in dev url instead. Localhost check commented out to prevent any possibility of the code running in prod */
        // window.location.hostname === 'localhost'
        //   ? 'https://dev.payaccount.io' :
        window.location.origin;
      // Fetch Galileo's direct render form URL
      const lastFourDigits = cardInfo.cardNumber.slice(-4);
      const {
        data: { url },
      } = await post(
        `/api/v1/cardholder_portal/cardholder/accounts/${account.id}/pin/direct_render`,
        {
          last_four_digits: lastFourDigits,
          // Galileo renders its hosted form in this locale. Without it the page
          // falls back to English regardless of the portal's language.
          locale: intl.locale,
          callback_url: `${callbackDomain}/profile?set_pin_state=success&account_id=${account.id}&last_four_digits=${lastFourDigits}`,
          failure_callback_url: `${callbackDomain}/profile?set_pin_state=failure&account_id=${account.id}&last_four_digits=${lastFourDigits}`,
        }
      );
      // Redirect to direct render form. Only an http(s) URL is followed.
      if (!assignSafeUrl(url)) {
        setShowCardInfoErrorAlert({
          type: 'danger',
          message: intl.formatMessage({ id: 'profile-failed-set-pin-alert' }),
        });
        setCardInfoIsLoading(false);
      }
    } catch (e) {
      setShowCardInfoErrorAlert({ type: 'danger', message: e.message });
      setCardInfoIsLoading(false);
    }
    // If setCardInfoIsLoading to false here, redirect will take place ~0.5 second after the loader disappears
  };

  const page = 'profile';
  const isVirtualProgram = programConfigs
    ? programConfigs.program_type.includes('virtual')
    : false;
  const allowSensitiveDataToken =
    programConfigs && programConfigs.allow_sensitive_data_token;
  const isNotGalileo =
    programProcessor && programProcessor.processor_name !== 'galileo';
  const isGalileo =
    programProcessor && programProcessor.processor_name === 'galileo';
  const isPinViewEnabled = 
    programConfigs && programConfigs.pin_view_enabled;
  const isPinSetEnabled = 
    programConfigs && programConfigs.pin_set_enabled;

  const isHRBClient = isHRB();

  const CardInfoLoader = () => (
    <LoaderContainer>
      <Loader />
      <Text
        text={profileEn[`${page}-loader-text`]}
        textFor="loader"
        page={page}
      />
    </LoaderContainer>
  );

  const CardInfoContent = () => (
    <>
      <PinUnblockBanner
        state={pinUnblock.state}
        nextAvailableAt={pinUnblock.nextAvailableAt}
      />
      {showCardInfoErrorAlert && (
        <Alert
          alert={showCardInfoErrorAlert.message}
          alertFor={showCardInfoErrorAlert.message}
          page={page}
          type={showCardInfoErrorAlert.type}
        />
      )}
      {iframeUrl ? (
        <SensitiveDataFrame
          src={iframeUrl}
          title={profileEn['profile-card-info-title']}
          sandbox="allow-scripts allow-same-origin"
        />
      ) : (
        <InputRow>
          <InputGroup
            label={intl.messages['profile-card-number-text']}
            labelFor="cardNumber"
            page={page}
          >
            <Input
              value={cardInfo.cardNumber}
              id="profile-cardNumber"
              disabled="disabled"
            />
          </InputGroup>
          <InputGroup
            label={intl.messages['profile-card-cvv-text']}
            labelFor="cvv"
            page={page}
          >
            <Input
              value={cardInfo.cvv}
              id="profile-cvv"
              disabled="disabled"
            />
          </InputGroup>
          <InputGroup
            label={intl.messages['profile-card-expiry-text']}
            labelFor="expiryDate"
            page={page}
          >
            <Input
              value={cardInfo.expiryDate}
              id="profile-expiryDate"
              disabled="disabled"
            />
          </InputGroup>
        </InputRow>
      )}
      {showGetPinModal && isNotGalileo && (
        <GetPinModal
          currentAccount={currentAccount}
          toggleModal={toggleGetPinModal}
          pin={pin}
          setPin={setPin}
          alert={pinModalAlert}
          setAlert={setPinModalAlert}
          initialPinFetch={initialPinFetch}
          allowSensitiveDataToken={!!allowSensitiveDataToken}
        />
      )}
      {showGetPinModal && isGalileo && (
        <GetGalileoPinModal
          currentAccount={currentAccount}
          toggleModal={toggleGetPinModal}
        />
      )}
      {showSetPinModal && (
        <SetPinModal
          pin={pin}
          setPin={setPin}
          currentAccount={currentAccount}
          toggleModal={toggleSetPinModal}
          alert={pinModalAlert}
          setAlert={setPinModalAlert}
          onPinChanged={onPinChanged}
        />
      )}
    </>
  );

  return (
    <Form as="div">
      <TitleWrapper>
        <Title
          title={profileEn['profile-card-info-title']}
          titleFor="card-info"
          page={page}
        />
        {!cardInfoIsLoading && (
          <ButtonGroup>
            {isPinViewEnabled && (
              <Button
                buttonText={profileEn[`${page}-view-pin-button`]}
                page={page}
                type="secondary"
                buttonFor="view-pin"
                onClick={e => toggleGetPinModal(e)}
              />
            )}
            <UnblockPinButton state={pinUnblock.state} onClick={pinUnblock.open} />
            {/* Both processors can set a PIN, by different mechanisms: Galileo
                redirects to its hosted direct-render form, everyone else posts the
                PIN straight through. The program flag decides whether the action is
                offered at all; the processor only decides which one runs, so the
                button waits until the processor is known. */}
            {isPinSetEnabled && programProcessor && (
              <Button
                buttonText={profileEn[`${page}-set-pin-button`]}
                page={page}
                type="secondary"
                buttonFor="set-pin"
                onClick={e =>
                  isGalileo ? fetchGalileoPinForm(e) : toggleSetPinModal()
                }
              />
            )}
            {(isHRBClient || isVirtualProgram) && allowSensitiveDataToken && (
              <Button
                buttonText={
                  profileEn['profile-show-sensitive-card-info-button']
                }
                page="profile"
                type="secondary"
                buttonFor="show-sensitive-card-info"
                onClick={fetchSensitive}
              />
            )}
          </ButtonGroup>
        )}
      </TitleWrapper>

      {/* Called, not mounted: both are defined inside CardInfo, so rendering them
          as elements makes React see a new component type every render and remount
          the subtree — which wipes the PIN fields on the mismatch error. */}
      {cardInfoIsLoading ? CardInfoLoader() : CardInfoContent()}
      <UnblockPinModal
        state={pinUnblock.state}
        submitting={pinUnblock.submitting}
        failed={pinUnblock.failed}
        onConfirm={pinUnblock.confirm}
        onCancel={pinUnblock.cancel}
      />
      {showPasswordConfirm && (
        <PasswordConfirmModal
          onConfirm={handlePasswordConfirm}
          onCancel={() => setShowPasswordConfirm(false)}
          error={passwordConfirmError}
        />
      )}
    </Form>
  );
};

CardInfo.propTypes = {
  onPinUnblocked: PropTypes.func,
  currentAccount: PropTypes.shape({
    id: PropTypes.number.isRequired,
    cards: PropTypes.array.isRequired,
  }),
  programProcessor: PropTypes.shape({
    processor_name: PropTypes.string.isRequired,
  }),
  programConfigs: PropTypes.shape({
    program_type: PropTypes.string.isRequired,
    allow_sensitive_data_token: PropTypes.bool.isRequired,
    pin_view_enabled: PropTypes.bool.isRequired,
    pin_set_enabled: PropTypes.bool.isRequired,
  }),
  // Shows the PIN-changed confirmation on the card banner, outside the modal.
  onPinChanged: PropTypes.func,
};

CardInfo.defaultProps = {
  currentAccount: null,
  programProcessor: null,
  programConfigs: null,
  onPinUnblocked: null,
  onPinChanged: () => {},
};
export default CardInfo;